/**
 * What an approval is about, in the words of the module that raised it.
 *
 * GET /v1/wf/instances/:id carries it as `subject` (backend m04
 * WorkflowSubjectRegistry). The web approval screen and the phone's inbox
 * detail both render it, so the shape and the way a typed value is written
 * live here, once, and neither can drift from the other or from the server.
 */
import { unitLabel } from './unit-label';

/** One value of a field, typed so the reader writes it in the tenant's format. */
export interface WorkflowSubjectValue {
  readonly value: string;
  /**
   * `money` is an amount, `quantity` a stored decimal measured in `unit` (a
   * unit code), `day` a stored business day (YYYY-MM-DD). Absent: shown as is.
   */
  readonly format?: 'money' | 'quantity' | 'day';
  readonly unit?: string;
}

/** One labelled line of the request, as the approver should read it. */
export interface WorkflowSubjectField {
  readonly label: string;
  /** The server's own rendering; what is shown when there are no parts. */
  readonly value: string;
  readonly parts?: readonly WorkflowSubjectValue[];
}

export interface WorkflowSubject {
  readonly title: string;
  readonly fields: readonly WorkflowSubjectField[];
  /** The field job behind the request, when there is one (R14). */
  readonly jobRequestId?: string | null;
  /** Files the request carries; `contentPath` is fetched with the reader's login. */
  readonly files?: readonly WorkflowSubjectFile[];
}

export interface WorkflowSubjectFile {
  readonly label: string;
  readonly contentPath: string;
}

/** The tenant's formatters, as useBusinessNumber and useBusinessDay bind them. */
export interface WorkflowSubjectFormatters {
  readonly money: (value: number) => string;
  readonly decimal: (value: string) => string;
  readonly day: (value: string) => string;
}

/** A field as one line of text, each typed part written the tenant's way. */
export function workflowSubjectFieldText(
  field: WorkflowSubjectField,
  fmt: WorkflowSubjectFormatters,
): string {
  if (!field.parts) return field.value;
  return field.parts
    .map((part) => {
      switch (part.format) {
        case 'money':
          return `₹ ${fmt.money(Number(part.value))}`;
        case 'quantity':
          return part.unit
            ? `${fmt.decimal(part.value)} ${unitLabel(part.unit)}`
            : fmt.decimal(part.value);
        case 'day':
          return fmt.day(part.value);
        default:
          return part.value;
      }
    })
    .join(' · ');
}
